import { GlassCard } from "@/components/GlassCard";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LineChart as ChartIcon } from "lucide-react";
import { fmtUSD } from "@/lib/format";

const CHART_MARGIN = { top: 10, right: 10, left: -20, bottom: 0 };
const TOOLTIP_CURSOR = { stroke: "rgba(255,255,255,0.15)" };
const TOOLTIP_STYLE = { borderRadius: 12 };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass px-3 py-2 text-xs" style={TOOLTIP_STYLE}>
      <p className="font-semibold text-white mb-1">Day {label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="tabular-nums">
          {p.name}: {fmtUSD(p.value)}
        </p>
      ))}
    </div>
  );
};

export function EarningsChart({ daily }) {
  const data = (daily || []).map((d) => ({
    date: d.date.slice(-2),
    Commission: d.commission,
    Bonus: d.bonus,
    "Base Pay": d.base_pay,
  }));

  return (
    <GlassCard data-testid="earnings-chart">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl glass-pill flex items-center justify-center">
          <ChartIcon className="w-5 h-5 text-sky-300" strokeWidth={2.2} />
        </div>
        <div>
          <p className="font-heading text-base font-bold text-white">Daily Earnings</p>
          <p className="text-xs text-white/55">Commission · Bonuses · Base pay</p>
        </div>
      </div>
      <div className="h-64 w-full">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-white/40 text-sm">
            No data yet — add a sale or mark a workday
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={CHART_MARGIN}>
              <defs>
                <linearGradient id="gCom" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.75} />
                  <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="gBon" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fb7185" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#fb7185" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="gBase" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.5)" fontSize={11} />
              <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR} />
              <Area type="monotone" dataKey="Commission" stackId="1" stroke="#38bdf8" fill="url(#gCom)" strokeWidth={2} isAnimationActive={false} />
              <Area type="monotone" dataKey="Bonus" stackId="1" stroke="#fb7185" fill="url(#gBon)" strokeWidth={2} isAnimationActive={false} />
              <Area type="monotone" dataKey="Base Pay" stackId="1" stroke="#a78bfa" fill="url(#gBase)" strokeWidth={2} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </GlassCard>
  );
}
